// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
// biome-ignore-all lint/suspicious/noImplicitAnyLet: TODO - address later
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import type { MetricCategory } from "@/components/metrics/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { WebsitePageMetrics } from "@/hooks/api/use-website-page-metrics";
import { formatDate } from "@/lib/date-formatting";
import { cn } from "@/lib/utils";

// Use predefined pill colors from CATEGORY_STYLES
const OPPORTUNITY_PILL_CLASSES = CATEGORY_STYLES.Opportunity.pillColor;
const ACCOUNT_PILL_CLASSES = CATEGORY_STYLES.Account.pillColor;

import { Search } from "lucide-react";
import React from "react";
import {
  type AccountData,
  formatAccountSourceRecordIdsForCSV,
  formatAccountsForCSV,
} from "@/lib/account-data";
import { formatCurrency } from "@/lib/metrics/format-currency";
import { formatNumber } from "@/lib/metrics/format-number";
import { formatPercentage } from "@/lib/metrics/format-percentage";
import {
  formatOpportunitiesForCSV,
  getOpportunityData,
} from "@/lib/opportunity-display-utils";
import type {
  EngagementPeriodItem,
  PersonItem,
} from "@/pages/campaigns/campaign-explorer/campaign-metrics-config";
import type { UnifiedTableColumn } from "@/types/table-column";
import { formatRelationSource } from "@/utils/formatters";

// Common types for drilldown items
export const DRILLDOWN_LIMIT = 5000;

// Define WIP metrics
export const WIP_METRICS: Array<keyof WebsitePageMetrics> = [];

// Define hidden metrics that should be fetched but not displayed
export const HIDDEN_METRICS: Array<keyof WebsitePageMetrics> = [
  "opportunities_influenced_amount",
  "opportunities_influenced_in_cycle_amount",
  "opportunities_created_post_engagement_amount",
  "progressed_opportunities_amount",
  "opportunities_influenced_won_amount",
  "opportunities_activated_amount",
  "opportunities_activated_won_amount",
  "engagements_per_deal_contact",
  "engagements_per_person",
  "engagements_per_account",
  "days_since_first_engagement",
  "days_since_latest_engagement",
  "first_engagement_date",
  "last_engagement_date",
];

// Define default metric order
export const DEFAULT_METRIC_ORDER: Array<keyof WebsitePageMetrics> = [
  "accounts_engaged",
  "engagements_per_account",
  "total_engagements",
  "people_engaged",
  "engagements_per_person",
  "deal_contacts_engaged",
  "engagements_per_deal_contact",
  "opportunities_influenced",
  "opportunities_influenced_amount",
  "opportunities_influenced_won",
  "opportunities_influenced_won_amount",
  "opportunities_activated",
  "opportunities_activated_amount",
  "opportunities_activated_won",
  "opportunities_activated_won_amount",
  "opportunities_created_post_engagement",
  "opportunities_created_post_engagement_amount",
  "opportunities_influenced_in_cycle",
  "opportunities_influenced_in_cycle_amount",
  "progressed_opportunities",
  "progressed_opportunities_amount",
  "accounts_activated",
  "accounts_reactivated",
  "accounts_without_opps",
  "first_engagement_date",
  "last_engagement_date",
  "webpage_active_days",
];

// Options for the rendering function
interface RenderMetricOptions {
  metricKey: keyof WebsitePageMetrics;
  metrics?: Partial<WebsitePageMetrics>;
  benchmarkMetrics?: Partial<WebsitePageMetrics>;
  isBenchmark?: boolean;
  isCurrency?: boolean;
  amountMetricKey?: keyof WebsitePageMetrics;
  secondaryMetricKey?: keyof WebsitePageMetrics;
  secondaryValue?: number;
  secondaryValueLabel?: React.ReactNode;
}

// Helper function to create consistent account link rendering
const renderAccountLink = (item: unknown): React.ReactNode => {
  const typedItem = item as {
    name?: string;
    account_name?: string;
    account_id: string;
  };
  if (!typedItem.account_id) {
    return "";
  }

  const displayName =
    typedItem.account_name || typedItem.name || "Unknown Account";

  // Create links for both explorer and milestone analysis
  const explorerUrl = `/accounts/${typedItem.account_id}/explorer`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <a
          href={explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-pill
          className={cn(
            "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
            ACCOUNT_PILL_CLASSES,
          )}
        >
          {displayName}
        </a>
      </TooltipTrigger>
      <TooltipContent>
        <p>View this account in Account Explorer</p>
      </TooltipContent>
    </Tooltip>
  );
};

// Helper function to create person link rendering
// Used in drilldown configurations
export const renderPersonLink = (item: unknown): React.ReactNode => {
  const typedItem = item as {
    first_name?: string;
    last_name?: string;
    email_address?: string;
    person_id?: string;
    account_id?: string;
  };

  const fullName = [typedItem.first_name, typedItem.last_name]
    .filter(Boolean)
    .join(" ");

  const displayName = fullName || typedItem.email_address || "Unknown";

  if (typedItem.account_id && typedItem.person_id) {
    const explorerUrl = `/accounts/${typedItem.account_id}/explorer?personId=${typedItem.person_id}`;

    return (
      <div>
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href={explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center hover:text-muted-foreground hover:bg-muted rounded px-2 py-1 -mx-2 -my-1 transition-colors group/name"
            >
              <div className="flex flex-col">
                <span className="font-medium">{displayName}</span>
                {typedItem.email_address && (
                  <span className="text-sm text-muted-foreground">
                    {typedItem.email_address}
                  </span>
                )}
              </div>
              <Search className="ml-2 h-4 w-4 text-muted-foreground transition-colors opacity-0 group-hover/name:opacity-100" />
            </a>
          </TooltipTrigger>
          <TooltipContent>
            <p>View this person in Account Explorer</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return <div className="font-medium">{displayName}</div>;
};

// Helper function to create opportunity link rendering
const renderOpportunityLink = (item: unknown): React.ReactNode => {
  const typedItem = item as {
    name?: string;
    opportunity_name?: string;
    opportunity_id: string;
    account_id: string;
  };

  if (!typedItem.opportunity_id) {
    return "";
  }

  const displayName =
    typedItem.name || typedItem.opportunity_name || "Unknown Opportunity";
  return (
    <div>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={`/accounts/${typedItem.account_id}/explorer?opportunityId=${typedItem.opportunity_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center hover:text-muted-foreground hover:bg-muted rounded px-2 py-1 -mx-2 -my-1 transition-colors group/name"
          >
            <span>{displayName}</span>
            <Search className="ml-2 h-4 w-4 text-muted-foreground transition-colors opacity-0 group-hover/name:opacity-100" />
          </a>
        </TooltipTrigger>
        <TooltipContent>
          <p>View this opportunity in Account Explorer</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

// Unified function for rendering metrics with various display options
export function renderMetric(
  value: number | string | null | undefined,
  options: RenderMetricOptions,
): React.ReactNode {
  // Handle null/undefined values
  if (value === undefined || value === null) return "—";

  // Convert string values to numbers if needed
  const numericValue = typeof value === "string" ? parseFloat(value) : value;

  // --- Primary Value Calculation ---
  const safeValue = numericValue;

  // Determine if we're formatting currency or standard number
  const isCurrency = options.isCurrency || options.metricKey.includes("amount");

  // Format the primary value appropriately
  let formattedValue;
  if (isCurrency) {
    formattedValue = formatCurrency(safeValue);
  } else if (
    options.metricKey.includes("percentage") ||
    options.metricKey.includes("rate")
  ) {
    formattedValue = formatPercentage(safeValue);
  } else if (options.metricKey.includes("date")) {
    formattedValue = safeValue ? formatDate(String(safeValue)) : "—";
  } else {
    formattedValue = formatNumber(safeValue);
  }

  // --- Secondary Content Calculation ---
  let secondaryContentJsx: React.ReactNode = null;

  // Check for secondary metrics
  if (options) {
    // Determine which secondary metric to use (amount or other secondary metric)
    const secondaryKey = options.amountMetricKey || options.secondaryMetricKey;
    const secondaryValue =
      secondaryKey && options.metrics
        ? options.metrics[secondaryKey]
        : options.secondaryValue;

    // Format the secondary value if it exists
    const formattedSecondaryValue =
      typeof secondaryValue === "number"
        ? options.amountMetricKey
          ? formatCurrency(secondaryValue)
          : secondaryValue.toLocaleString("en-US", {
              maximumFractionDigits: 1,
            })
        : null;

    // CASE 1: Custom Label (passed via secondaryValueLabel without secondaryValue/Key)
    if (
      options.secondaryValueLabel &&
      !secondaryKey &&
      options.secondaryValue === undefined
    ) {
      secondaryContentJsx = (
        <span
          className="font-normal whitespace-nowrap"
          title={
            typeof options.secondaryValueLabel === "string"
              ? options.secondaryValueLabel
              : undefined
          }
        >
          {options.secondaryValueLabel}
        </span>
      );
    }
    // CASE 2: Numeric Secondary Value (Amount or Ratio) - Only show if primary value > 0
    else if (
      (secondaryKey || typeof options.secondaryValue === "number") &&
      numericValue > 0
    ) {
      // Create tooltip text for the full secondary metric
      const tooltipText =
        formattedSecondaryValue &&
        options.secondaryValueLabel &&
        typeof options.secondaryValueLabel === "string"
          ? `${formattedSecondaryValue}${options.secondaryValueLabel}`
          : undefined;

      secondaryContentJsx = (
        <span className="inline-flex items-center gap-1" title={tooltipText}>
          {/* Display secondary value */}
          <span className="font-bold">{formattedSecondaryValue}</span>

          {/* Display label if provided */}
          {options.secondaryValueLabel &&
            typeof options.secondaryValueLabel === "string" && (
              <span className="font-normal whitespace-nowrap">
                {options.secondaryValueLabel}
              </span>
            )}
        </span>
      );
    }
  }

  // --- Final JSX Structure ---
  return (
    <div className="flex flex-col">
      {/* Primary Value */}
      <div className="relative flex items-start">
        <span className="text-2xl sm:text-3xl md:text-2xl lg:text-3xl font-bold leading-none">
          {formattedValue}
        </span>
      </div>

      {/* Secondary Content (if any) */}
      <div className="min-h-[20px] sm:min-h-[24px] md:min-h-[20px] lg:min-h-[24px] text-sm sm:text-base md:text-sm lg:text-base text-muted-foreground/50">
        {secondaryContentJsx}
      </div>
    </div>
  );
}

// Define metric data types
export type MetricDataType = "integer" | "float" | "currency" | "date";

// Mapping of each metric to its data type
export const METRIC_DATA_TYPES: Record<
  keyof WebsitePageMetrics,
  MetricDataType
> = {
  accounts_engaged: "integer",
  total_engagements: "integer",
  people_engaged: "integer",
  opportunities_influenced: "integer",
  opportunities_influenced_amount: "currency",
  opportunities_influenced_in_cycle: "integer",
  opportunities_influenced_in_cycle_amount: "currency",
  opportunities_created_post_engagement: "integer",
  opportunities_created_post_engagement_amount: "currency",
  progressed_opportunities: "integer",
  progressed_opportunities_amount: "currency",
  deal_contacts_engaged: "integer",
  engagements_per_deal_contact: "float",
  accounts_reactivated: "integer",
  accounts_activated: "integer",
  engagements_per_person: "float",
  opportunities_influenced_won: "integer",
  opportunities_influenced_won_amount: "currency",
  accounts_without_opps: "integer",
  opportunities_activated: "integer",
  opportunities_activated_amount: "currency",
  opportunities_activated_won: "integer",
  opportunities_activated_won_amount: "currency",
  engagements_per_account: "float",
  first_engagement_date: "date",
  last_engagement_date: "date",
  days_since_first_engagement: "integer",
  days_since_latest_engagement: "integer",
  webpage_active_days: "integer",
};

// Define metric type including drilldown configuration
interface MetricConfig {
  title: string;
  tooltip?: string;
  category: MetricCategory;
  render: (
    value: number | string | null | undefined,
    options: RenderMetricOptions,
  ) => React.ReactNode;
  drilldownType: "table" | "chart";
  primaryKey: string[];
  drilldownColumns: UnifiedTableColumn[];
  defaultSort?: { key: string; direction: "asc" | "desc" };
}

// Configuration for each metric
export const METRIC_CONFIG: Record<keyof WebsitePageMetrics, MetricConfig> = {
  accounts_engaged: {
    title: "Accounts Engaged",
    tooltip: "Count of accounts where any person viewed this page.",
    category: "Account",
    drilldownType: "table",
    primaryKey: ["account_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        secondaryMetricKey: "engagements_per_account",
        secondaryValueLabel: ` ${
          options.metrics?.engagements_per_account === 1
            ? "engagement"
            : "engagements"
        }/account`,
      });
    },
    drilldownColumns: [
      {
        key: "name",
        title: "Account Name",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_engagements", direction: "desc" },
  },
  total_engagements: {
    title: "Total Engagements",
    tooltip:
      "Count of total page views for this web page (may include more than one per person). Excludes any page views from your own team.",
    category: "Touchpoint",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "table",
    primaryKey: ["engagement_date", "person_id"],
    drilldownColumns: [
      {
        key: "engagement_date",
        dataType: "date",
        title: "Date",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { engagement_date?: string };
          return typedItem.engagement_date
            ? formatDate(typedItem.engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { engagement_date?: string };
          return typedItem.engagement_date
            ? formatDate(typedItem.engagement_date)
            : "";
        },
      },
      {
        key: "person",
        title: "Person",
        sortable: true,
        filterable: false,
        sortKey: "first_name",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as PersonItem & {
            person_id?: string;
            account_id?: string;
          };
          const fullName = [typedItem.first_name, typedItem.last_name]
            .filter(Boolean)
            .join(" ");
          const displayName = fullName || "Unknown";

          if (typedItem.account_id && typedItem.person_id) {
            const explorerUrl = `/accounts/${typedItem.account_id}/explorer?personId=${typedItem.person_id}`;
            return (
              <div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={explorerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center hover:text-muted-foreground hover:bg-muted rounded px-2 py-1 -mx-2 -my-1 transition-colors group/name"
                    >
                      <span>{displayName}</span>
                      <Search className="ml-2 h-4 w-4 text-muted-foreground transition-colors opacity-0 group-hover/name:opacity-100" />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View this person in Account Explorer</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            );
          }
          return <span>{displayName}</span>;
        },
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "email_address",
        title: "Email",
        sortable: true,
        filterable: false,
      },
      {
        key: "accounts",
        title: "Accounts",
        sortable: false,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          if (!typedItem.accounts || typedItem.accounts.length === 0) {
            return "-";
          }
          return (
            <span>
              {typedItem.accounts.map((account, idx) => (
                <React.Fragment key={account.id}>
                  {idx > 0 && ", "}
                  {renderAccountLink({
                    account_id: account.id,
                    name: account.name,
                  })}
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountsForCSV(typedItem.accounts);
        },
      },
      {
        key: "account_source_record_ids",
        title: "Account Source IDs",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountSourceRecordIdsForCSV(typedItem.accounts);
        },
      },
      {
        key: "web_session_channel",
        title: "Channel",
        sortable: true,
        filterable: false,
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "engagement_date", direction: "desc" },
  },
  people_engaged: {
    title: "People Engaged",
    tooltip:
      "Count of people who viewed this page. Excludes any people from your own team.",
    category: "Person",
    drilldownType: "table",
    primaryKey: ["person_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        secondaryMetricKey: "engagements_per_person",
        secondaryValueLabel: ` ${
          options.metrics?.engagements_per_person === 1
            ? "engagement"
            : "engagements"
        }/person`,
      });
    },
    drilldownColumns: [
      {
        key: "person_id",
        title: "Person",
        sortable: true,
        filterable: false,
        sortKey: "first_name",
        render: renderPersonLink,
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "email_address",
        title: "Email Address",
        exportOnly: true,
      },
      {
        key: "job_title",
        title: "Job Title",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as PersonItem;
          if (!typedItem.job_title)
            return (
              <span className="italic text-muted-foreground">No data</span>
            );
          return <span>{typedItem.job_title}</span>;
        },
      },
      {
        key: "accounts",
        title: "Accounts",
        sortable: false,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          if (!typedItem.accounts || typedItem.accounts.length === 0) {
            return "-";
          }
          // Render all accounts as comma-separated links
          return (
            <span>
              {typedItem.accounts.map((account, idx) => (
                <React.Fragment key={account.id}>
                  {idx > 0 && ", "}
                  {renderAccountLink({
                    account_id: account.id,
                    name: account.name,
                  })}
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: AccountData[];
          };
          return formatAccountsForCSV(typedItem.accounts);
        },
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "account_source_record_ids",
        title: "Account Source IDs",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountSourceRecordIdsForCSV(typedItem.accounts);
        },
      },
    ],
    defaultSort: { key: "total_engagements", direction: "desc" },
  },
  deal_contacts_engaged: {
    title: "Deal Contacts Engaged",
    tooltip:
      "Count of people who viewed this page and have been in the buying group for any opportunity. Includes buying group associations via both CRM contact roles and Upside's healed buying groups. Excludes any people from your own team.",
    category: "Person",
    drilldownType: "table",
    primaryKey: ["person_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        secondaryMetricKey: "engagements_per_deal_contact",
        secondaryValueLabel: ` ${
          options.metrics?.engagements_per_deal_contact === 1
            ? "engagement"
            : "engagements"
        }/contact`,
      });
    },
    drilldownColumns: [
      {
        key: "person_name",
        title: "Contact",
        sortable: true,
        filterable: false,
        sortKey: "first_name",
        render: renderPersonLink,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "accounts",
        title: "Accounts",
        sortable: false,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          if (!typedItem.accounts || typedItem.accounts.length === 0) {
            return "-";
          }
          // Render all accounts as comma-separated links
          return (
            <span>
              {typedItem.accounts.map((account, idx) => (
                <React.Fragment key={account.id}>
                  {idx > 0 && ", "}
                  {renderAccountLink({
                    account_id: account.id,
                    name: account.name,
                  })}
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: AccountData[];
          };
          return formatAccountsForCSV(typedItem.accounts);
        },
      },
      {
        key: "account_source_record_ids",
        title: "Account Source IDs",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountSourceRecordIdsForCSV(typedItem.accounts);
        },
      },
      {
        key: "job_title",
        title: "Job Title",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as PersonItem;
          if (!typedItem.job_title)
            return (
              <span className="italic text-muted-foreground">No data</span>
            );
          return <span>{typedItem.job_title}</span>;
        },
      },
      {
        key: "relation_source",
        title: "Relation Source",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as PersonItem;
          return (
            <span>
              {typedItem.relation_source
                ? formatRelationSource(typedItem.relation_source)
                : ""}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as PersonItem;
          return typedItem.relation_source
            ? formatRelationSource(typedItem.relation_source)
            : "";
        },
      },
      {
        key: "opportunities",
        title: "Opportunities",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            opportunities_involved?: unknown;
            opportunity_ids?: unknown;
            opportunities?: Array<{
              id: string;
              name: string;
              account_id: string;
            }>;
            account_id?: string;
          };

          // Check for new JSON format first
          if (
            typedItem.opportunities &&
            Array.isArray(typedItem.opportunities)
          ) {
            if (typedItem.opportunities.length === 0) return "-";

            return (
              <div className="flex flex-wrap gap-1">
                {typedItem.opportunities.map((opportunity) => {
                  const {
                    id: oppId,
                    name,
                    account_id: accountId,
                  } = opportunity;
                  return (
                    <Tooltip key={oppId}>
                      <TooltipTrigger asChild>
                        <a
                          href={`/accounts/${accountId}/explorer?opportunityId=${oppId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-pill
                          className={cn(
                            "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
                            OPPORTUNITY_PILL_CLASSES,
                          )}
                        >
                          {name}
                        </a>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>View this opportunity in Account Explorer</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            );
          }

          // Fall back to legacy format
          const value = typedItem.opportunities_involved;
          if (!value) return "-";

          const { names: opportunityNames, ids: opportunityIds } =
            getOpportunityData(typedItem);
          const accountId = typedItem.account_id;

          return (
            <div className="flex flex-wrap gap-1">
              {opportunityNames.map((name, index) => {
                const oppId = opportunityIds[index];

                if (oppId && accountId) {
                  return (
                    <Tooltip key={oppId}>
                      <TooltipTrigger asChild>
                        <a
                          href={`/accounts/${accountId}/explorer?opportunityId=${oppId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-pill
                          className={cn(
                            "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
                            OPPORTUNITY_PILL_CLASSES,
                          )}
                        >
                          {name}
                        </a>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>View this opportunity in Account Explorer</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                }
                // Fallback to non-clickable badge if no ID
                return (
                  <span
                    key={index}
                    className="inline-flex items-center rounded-md bg-supporting-blue/10 px-2 py-1 text-xs font-medium text-supporting-blue ring-1 ring-inset ring-supporting-blue/10 dark:bg-supporting-blue/10 dark:text-supporting-blue dark:ring-supporting-blue/30"
                  >
                    {name}
                  </span>
                );
              })}
            </div>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            opportunities_involved?: unknown;
            opportunities?: Array<{
              id: string;
              name: string;
              account_id: string;
            }>;
          };

          // Check for new JSON format first
          if (
            typedItem.opportunities &&
            Array.isArray(typedItem.opportunities)
          ) {
            return formatOpportunitiesForCSV(typedItem.opportunities);
          }

          // Fall back to legacy format
          return formatOpportunitiesForCSV(typedItem.opportunities_involved);
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_engagements", direction: "desc" },
  },
  engagements_per_deal_contact: {
    title: "Engagements per Deal Contact",
    tooltip:
      "Average number of page views per buying group person. Includes buying group associations via both CRM contact roles and Upside's healed buying groups. Excludes any people from your own team.",
    category: "Person",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "table",
    primaryKey: [],
    drilldownColumns: [],
  },
  opportunities_influenced: {
    title: "Opportunities Influenced",
    tooltip:
      "Count and total amount of opportunities influenced by this web page.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        amountMetricKey: "opportunities_influenced_amount",
      });
    },
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        sortKey: "opportunity_name",
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_engagements", direction: "desc" },
  },
  opportunities_influenced_amount: {
    title: "Sum of Amounts for All Opportunities Influenced",
    tooltip:
      "Sum of the total amount of all opportunities influenced by this web page.",
    category: "Opportunity",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "table",
    primaryKey: [],
    drilldownColumns: [],
  },
  opportunities_influenced_in_cycle: {
    title: "Opportunities Influenced In-Cycle",
    tooltip:
      "Count and total amount of opportunities influenced by this web page during their active cycle.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        amountMetricKey: "opportunities_influenced_in_cycle_amount",
      });
    },
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        sortKey: "opportunity_name",
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { total_amount?: number };
          return typedItem.total_amount
            ? formatCurrency(typedItem.total_amount)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { total_amount?: number };
          return typedItem.total_amount
            ? formatCurrency(typedItem.total_amount)
            : "";
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_engagements", direction: "desc" },
  },
  opportunities_influenced_in_cycle_amount: {
    title: "Sum of Amounts for Opportunities Influenced In-Cycle",
    tooltip:
      "Sum of the total amount of opportunities influenced by this web page during their active cycle.",
    category: "Opportunity",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "table",
    primaryKey: [],
    drilldownColumns: [],
  },
  opportunities_created_post_engagement: {
    title: "Opportunities Created Post-Engagement",
    tooltip:
      "Count and total amount of opportunities that were created after page view.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        amountMetricKey: "opportunities_created_post_engagement_amount",
      });
    },
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "days_till_creation",
        title: "Days from Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const value = item as { days_till_creation?: number | null };
          return value.days_till_creation !== null &&
            value.days_till_creation !== undefined
            ? `${value.days_till_creation} days`
            : "N/A";
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { created_date?: string };
          return value.created_date ? formatDate(value.created_date) : "-";
        },
        exportValue: (item: unknown): string => {
          const value = item as { created_date?: string };
          return value.created_date ? formatDate(value.created_date) : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { close_date?: string };
          return value.close_date ? formatDate(value.close_date) : "-";
        },
        exportValue: (item: unknown): string => {
          const value = item as { close_date?: string };
          return value.close_date ? formatDate(value.close_date) : "";
        },
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_amount", direction: "desc" },
  },
  opportunities_created_post_engagement_amount: {
    title: "Sum of Amounts for Opportunities Created Post-Engagement",
    tooltip:
      "Sum of the total amount of opportunities that were created after page view.",
    category: "Opportunity",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "table",
    primaryKey: [],
    drilldownColumns: [],
  },
  progressed_opportunities: {
    title: "Progressed Opportunities",
    tooltip:
      "Count and total amount of opportunities that progressed after page view.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        amountMetricKey: "progressed_opportunities_amount",
      });
    },
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "stage_progression",
        title: "Stage Progression",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            stage_progression?: string;
            stage_at_engagement?: string;
          };
          if (!typedItem.stage_progression) return "-";

          const stages = typedItem.stage_progression.split(" → ");
          const engagementStageIndex = stages.indexOf(
            typedItem.stage_at_engagement ?? "",
          );

          return (
            <span>
              {stages.map((stage, index) => (
                <React.Fragment key={index}>
                  {index > 0 && " → "}
                  <span
                    className={
                      engagementStageIndex >= 0 && index > engagementStageIndex
                        ? "font-bold"
                        : ""
                    }
                  >
                    {stage}
                  </span>
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { stage_progression?: string };
          return typedItem.stage_progression || "-";
        },
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "progressed_date",
        dataType: "date",
        title: "Progressed Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { progressed_date?: string };
          return value.progressed_date
            ? formatDate(value.progressed_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const value = item as { progressed_date?: string };
          return value.progressed_date
            ? formatDate(value.progressed_date)
            : "-";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "progressed_date", direction: "desc" },
  },
  progressed_opportunities_amount: {
    title: "Sum of Amounts for Progressed Opportunities",
    tooltip:
      "Sum of the total amount of opportunities that progressed after page view.",
    category: "Opportunity",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "table",
    primaryKey: [],
    drilldownColumns: [],
  },
  opportunities_influenced_won: {
    title: "Won Opportunities Influenced",
    tooltip:
      "Count and total amount of won opportunities influenced by this web page.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        amountMetricKey: "opportunities_influenced_won_amount",
      });
    },
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        sortKey: "opportunity_name",
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date: string };
          return formatDate(typedItem.created_date);
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date: string };
          return formatDate(typedItem.close_date);
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_engagements", direction: "desc" },
  },
  opportunities_influenced_won_amount: {
    title: "Sum of Amounts for Won Opportunities Influenced",
    tooltip:
      "Sum of the total amount of won opportunities influenced by this web page.",
    category: "Opportunity",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "table",
    primaryKey: [],
    drilldownColumns: [],
  },
  opportunities_activated: {
    title: "Opportunities with activation milestone citation",
    tooltip:
      "Count of opportunities where AI analysis identified this web page's touchpoints as influential in opportunity activation.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        amountMetricKey: "opportunities_activated_amount",
      });
    },
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        sortKey: "opportunity_name",
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date: string };
          return formatDate(typedItem.created_date);
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date: string };
          return formatDate(typedItem.close_date);
        },
      },
      {
        key: "is_won",
        title: "Won",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { is_won: boolean };
          return value.is_won ? "Yes" : "No";
        },
      },
      {
        key: "analysis_type",
        title: "Analysis Type",
        sortable: true,
        filterable: false,
      },
      {
        key: "analysis_created_date",
        dataType: "date",
        title: "Analysis Date",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { analysis_created_date: string };
          return formatDate(typedItem.analysis_created_date);
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { analysis_created_date: string };
          return formatDate(typedItem.analysis_created_date);
        },
      },
      {
        key: "influence_source",
        title: "Influence Source",
        sortable: true,
        filterable: false,
      },
      {
        key: "summary",
        title: "Analysis Summary",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { summary: string };
          if (!typedItem.summary) return null;
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-sm text-muted-foreground truncate max-w-xs cursor-help">
                  {typedItem.summary.length > 50
                    ? `${typedItem.summary.substring(0, 50)}...`
                    : typedItem.summary}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <p>{typedItem.summary}</p>
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "created_date", direction: "desc" },
  },
  opportunities_activated_amount: {
    title: "Sum of Amounts for All Opportunities Activated",
    tooltip:
      "Sum of the total amount of all opportunities where AI analysis identified this web page's touchpoints as influential in opportunity activation.",
    category: "Opportunity",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "table",
    primaryKey: [],
    drilldownColumns: [],
  },
  opportunities_activated_won: {
    title: "Won opportunities with activation milestone citation",
    tooltip:
      "Count of won opportunities where AI analysis identified this web page's touchpoints as influential in opportunity activation.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        amountMetricKey: "opportunities_activated_won_amount",
      });
    },
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        sortKey: "opportunity_name",
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { total_amount?: number };
          return typedItem.total_amount
            ? formatCurrency(typedItem.total_amount)
            : "-";
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "-";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "-";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "-";
        },
      },
      {
        key: "analysis_type",
        title: "Analysis Type",
        sortable: true,
        filterable: false,
      },
      {
        key: "influence_source",
        title: "Influence Source",
        sortable: true,
        filterable: false,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "created_date", direction: "desc" },
  },
  opportunities_activated_won_amount: {
    title: "Sum of Amounts for Won Opportunities Activated",
    tooltip:
      "Sum of the total amount of won opportunities where AI analysis identified this web page's touchpoints as influential in opportunity activation.",
    category: "Opportunity",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "table",
    primaryKey: [],
    drilldownColumns: [],
  },
  accounts_activated: {
    title: "Accounts Activated",
    tooltip:
      "Count of accounts that had no engagement at any time prior to viewing this web page.",
    category: "Account",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "table",
    primaryKey: ["account_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Account Name",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_engagements",
        title: "Page Views",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { total_engagements?: number };
          return typedItem.total_engagements !== undefined
            ? typedItem.total_engagements
            : "-";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { unique_people?: number };
          return typedItem.unique_people !== undefined
            ? typedItem.unique_people
            : "-";
        },
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "last_engagement_date", direction: "desc" },
  },
  accounts_reactivated: {
    title: "Accounts reactivated",
    tooltip:
      "Count of accounts that had no engagement for 90 days prior to viewing this web page.",
    category: "Account",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "table",
    primaryKey: ["account_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Account Name",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_engagements",
        title: "Page Views",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { total_engagements?: number };
          return typedItem.total_engagements !== undefined
            ? typedItem.total_engagements
            : "-";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { unique_people?: number };
          return typedItem.unique_people !== undefined
            ? typedItem.unique_people
            : "-";
        },
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "last_engagement_date", direction: "desc" },
  },
  accounts_without_opps: {
    title: "Accounts Engaged Outside of Opportunity Cycle",
    tooltip:
      "Count of accounts where all page views happened while no matching opportunity was open.",
    category: "Account",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "table",
    primaryKey: ["account_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Account Name",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_engagements",
        title: "Page Views",
        sortable: true,
        filterable: false,
      },
      {
        key: "people_engaged",
        title: "People Engaged",
        sortable: true,
        filterable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        sortable: false,
        filterable: false,
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_engagements", direction: "desc" },
  },
  engagements_per_person: {
    title: "Engagements per Person",
    tooltip:
      "Average number of page views per person who viewed this web page. Excludes any people from your own team.",
    category: "Person",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "table",
    primaryKey: [],
    drilldownColumns: [],
  },
  engagements_per_account: {
    title: "Engagements per Account",
    tooltip:
      "Average number of page views per account (total page views divided by number of accounts engaged).",
    category: "Account",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "table",
    primaryKey: [],
    drilldownColumns: [],
  },
  webpage_active_days: {
    title: "Webpage active days",
    tooltip:
      "Number of days between the first and last page view for this webpage.",
    category: "Other",
    drilldownType: "chart",
    primaryKey: ["date"],
    render: (_value, options) => {
      // Calculate primary value and date range string
      const primaryValue = options.metrics?.webpage_active_days ?? null;
      const dateRangeStr =
        options.metrics?.first_engagement_date &&
        options.metrics?.last_engagement_date
          ? `${formatDate(
              options.metrics.first_engagement_date,
            )} - ${formatDate(options.metrics.last_engagement_date)}`
          : "No engagement dates";

      // Call renderMetric, passing date range as secondaryValueLabel
      return renderMetric(primaryValue, {
        ...options,
        secondaryValueLabel: dateRangeStr,
      });
    },
    drilldownColumns: [
      {
        key: "date",
        dataType: "date",
        title: "Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { date?: string };
          return typedItem.date ? formatDate(typedItem.date) : "";
        },
      },
      {
        key: "engagement_count",
        title: "Daily Page Views",
        sortable: true,
        filterable: false,
      },
      {
        key: "unique_people",
        title: "Unique Visitors",
        sortable: true,
        filterable: false,
      },
      {
        key: "cumulative_engagements",
        title: "Cumulative Page Views",
        sortable: true,
        filterable: false,
      },
      {
        key: "cumulative_people",
        title: "Cumulative Visitors",
        sortable: true,
        filterable: false,
      },
    ],
    defaultSort: { key: "date", direction: "asc" },
  },
  first_engagement_date: {
    title: "First engagement date",
    tooltip: "Date of the first page view.",
    category: "Other",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "table",
    primaryKey: [],
    drilldownColumns: [],
  },
  last_engagement_date: {
    title: "Last engagement date",
    tooltip: "Date of the most recent page view.",
    category: "Other",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "table",
    primaryKey: [],
    drilldownColumns: [],
  },
  days_since_first_engagement: {
    title: "Days since first engagement",
    tooltip:
      "Days since this web page first began generating page views, compared to current date. This is useful to understand if enough time has elapsed to assess web page performance.",
    category: "Other",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "chart",
    primaryKey: ["date"],
    drilldownColumns: [
      {
        key: "date",
        dataType: "date",
        title: "Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { date?: string };
          return typedItem.date ? formatDate(typedItem.date) : "";
        },
      },
      {
        key: "engagement_count",
        title: "Page Views",
        sortable: true,
        filterable: false,
      },
    ],
    defaultSort: { key: "date", direction: "asc" },
  },
  days_since_latest_engagement: {
    title: "Days since latest engagement",
    tooltip:
      "Days since this web page most recently generated page views, compared to current date. This is useful to understand if the web page is active.",
    category: "Other",
    render: (value, options) => renderMetric(value, options),
    drilldownType: "chart",
    primaryKey: ["date"],
    drilldownColumns: [
      {
        key: "date",
        dataType: "date",
        title: "Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { date?: string };
          return typedItem.date ? formatDate(typedItem.date) : "";
        },
      },
      {
        key: "engagement_count",
        title: "Page Views",
        sortable: true,
        filterable: false,
      },
    ],
    defaultSort: { key: "date", direction: "asc" },
  },
};

// Re-export aliases for backward compatibility
export const WEBPAGE_METRIC_CONFIG = METRIC_CONFIG;
export const DEFAULT_WEBPAGE_METRIC_ORDER = DEFAULT_METRIC_ORDER;
export const HIDDEN_WEBPAGE_METRICS = HIDDEN_METRICS;

// Helper function to render metric values (backward compatibility)
export function renderWebpageMetric(
  metricKey: keyof WebsitePageMetrics,
  metrics?: Partial<WebsitePageMetrics>,
): React.ReactNode {
  const value = metrics?.[metricKey];
  return renderMetric(value, {
    metricKey,
    metrics: metrics as WebsitePageMetrics,
  });
}
